import { useParams, useNavigate } from 'react-router-dom';
import { RiArrowLeftLine } from 'react-icons/ri';
import { Button, Card, Skeleton } from '@/components/ui';
import { SEO } from '@/components/seo/SEO';
import { JobBadge } from '@/components/job/JobBadge/JobBadge';
import { QualityBadge } from '@/components/job/QualityBadge/QualityBadge';
import { RewardDisplay } from '@/components/job/RewardDisplay/RewardDisplay';
import { DeadlineCountdown } from '@/components/job/DeadlineCountdown/DeadlineCountdown';
import { DocumentPreview } from '@/components/job/DocumentPreview/DocumentPreview';
import { useJob, useClaimJob } from '@/api/worker';
import { ROUTES } from '@/constants/routes';
import styles from './JobDetailPage.module.css';

export function JobDetailPage() {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const { data: job, isLoading } = useJob(jobId!);
    const claimMutation = useClaimJob();

    if (isLoading) {
        return (
            <div className={styles.container}>
                <Skeleton width={140} height={14} style={{ marginBottom: 16 }} />
                <Card className={styles.card}>
                    <div className={styles.header}>
                        <Skeleton width="60%" height={28} />
                        <div className={styles.badges}>
                            <Skeleton width={90} height={26} variant="rounded" />
                            <Skeleton width={110} height={26} variant="rounded" />
                        </div>
                    </div>

                    <div className={styles.meta}>
                        <Skeleton width={120} height={28} variant="rounded" />
                        <Skeleton width={70} height={24} variant="rounded" />
                        <Skeleton width={160} height={16} />
                    </div>

                    <div className={styles.section}>
                        <Skeleton width={120} height={13} style={{ marginBottom: 10 }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="95%" />
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="70%" />
                        </div>
                    </div>

                    <div className={styles.section}>
                        <Skeleton width={140} height={13} style={{ marginBottom: 10 }} />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Skeleton variant="text" width="100%" />
                            <Skeleton variant="text" width="85%" />
                            <Skeleton variant="text" width="90%" />
                        </div>
                    </div>

                    <div className={styles.section}>
                        <Skeleton width={180} height={13} style={{ marginBottom: 10 }} />
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Skeleton width={100} height={28} variant="rounded" />
                            <Skeleton width={80} height={28} variant="rounded" />
                        </div>
                    </div>

                    <div className={styles.section}>
                        <Skeleton width={110} height={13} style={{ marginBottom: 10 }} />
                        <Skeleton width="100%" height={56} variant="card" />
                    </div>

                    <div className={styles.actions}>
                        <Skeleton width={180} height={44} variant="rounded" />
                    </div>
                </Card>
            </div>
        );
    }

    if (!job) {
        return (
            <div className={styles.notFound}>
                <h2>Trabajo no encontrado</h2>
                <Button onClick={() => navigate(ROUTES.home)}>Volver al inicio</Button>
            </div>
        );
    }

    const handleClaim = () => {
        claimMutation.mutate(job.uuid);
    };

    return (
        <div className={styles.container}>
            <SEO title={job.title} description={job.description.slice(0, 160)} path={`/home/${job.uuid}`} noindex />
            <button className={styles.back} onClick={() => navigate(ROUTES.home)}>
                <RiArrowLeftLine className={styles.backIcon} />
                Volver al inicio
            </button>

            <Card className={styles.card}>
                <div className={styles.header}>
                    <h1 className={styles.title}>{job.title}</h1>
                    <div className={styles.badges}>
                        <QualityBadge tier={job.quality_tier} />
                        <DeadlineCountdown deadline={job.deadline} />
                    </div>
                </div>

                <div className={styles.meta}>
                    <RewardDisplay amount={job.reward_amount} currency={job.currency} size="lg" />
                    <span className={styles.mode}>
                        {job.mode === 'open' ? 'Abierto' : job.mode === 'contest' ? 'Concurso' : 'Exclusivo'}
                    </span>
                    {job.max_workers > 1 && (
                        <span className={styles.slots}>
                            {job.active_assignment_count}/{job.max_workers} posiciones ocupadas
                        </span>
                    )}
                </div>

                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Descripcion</h3>
                    <p className={styles.description}>{job.description}</p>
                </div>

                {job.required_professions.length > 0 && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Profesiones requeridas</h3>
                        <div className={styles.tags}>
                            {job.required_professions.map((p) => (
                                <JobBadge key={p.uuid} profession={p} />
                            ))}
                        </div>
                    </div>
                )}

                {job.documents.length > 0 && (
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Documentos</h3>
                        <div className={styles.docGrid}>
                            {job.documents.map((doc) => (
                                <DocumentPreview key={doc.uuid} document={doc} />
                            ))}
                        </div>
                    </div>
                )}

                <div className={styles.actions}>
                    {job.has_claimed ? (
                        <Button variant="secondary" disabled>
                            Ya reclamado
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            onClick={handleClaim}
                            disabled={claimMutation.isPending}
                        >
                            {claimMutation.isPending ? 'Reclamando...' : 'Reclamar Trabajo'}
                        </Button>
                    )}
                </div>
            </Card>
        </div>
    );
}
